import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Box, Button, IconButton, Modal, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
  API_add_manual_actual_sales,
  API_get_actual_sales_daily,
  API_get_default_operating_hours,
  API_get_actual_used_schedule,
} from "../constants";
import { getDefaultAxiosConfig } from "../utilities/requests";
import RightworkSelect from "../components/shared/RightworkSelect";
import {
  canShowManualSalesReminder, previousBusinessDate, missingSalesDates,
  offsetSalesDate, formatSalesDate, SALES_REMINDER_LOOKBACK_DAYS, validSalesAmount,
} from "./manualSalesReminderUtils";

const primaryButtonSx = {
  textTransform: "none", borderRadius: "12px", backgroundColor: "#6d28d9",
  "&:hover": { backgroundColor: "#5b21b6" },
};

// Page memory survives navigation/store switches but resets on a full refresh.
export const deferredSalesReminders = new Set();

function SalesReminderForDate({ store, date: yesterday, userId, onSaved, bannerLeft }) {
  const dismissalKey = `rightwork:manual-sales:${userId}:${store.id}:${yesterday}`;
  const [missingDates, setMissingDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const date = missingDates.includes(selectedDate) ? selectedDate : missingDates[0];
  const amount = drafts[date] ?? "";
  const setAmount = (value) => setDrafts((current) => ({ ...current, [date]: value }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dismissed = useRef(false);
  const mounted = useRef(false);
  const requestVersion = useRef(0);
  const savingRef = useRef(false);
  const startDate = offsetSalesDate(yesterday, 1 - SALES_REMINDER_LOOKBACK_DAYS);
  const salesUrl = `${API_get_actual_sales_daily}${store.id}&date_start=${startDate}&date_end=${yesterday}`;
  const includeYesterday = !store.time_entries_enabled;

  useEffect(() => {
    mounted.current = true;
    const check = async () => {
      if (savingRef.current || deferredSalesReminders.has(dismissalKey)) return;
      const version = ++requestVersion.current;
      try {
        const config = getDefaultAxiosConfig();
        const query = `${store.id}&date_start=${startDate}&date_end=${yesterday}`;
        const [sales, hours, actuals] = await Promise.all([
          axios.get(salesUrl, config),
          axios.get(`${API_get_default_operating_hours}${query}&resolve_by_date=true`, config),
          // Calendar clock-ins early the next morning can belong to yesterday.
          axios.get(`${API_get_actual_used_schedule}${store.id}&date_start=${startDate}&date_end=${offsetSalesDate(yesterday, 1)}`, config),
        ]);
        if (!mounted.current || version !== requestVersion.current || deferredSalesReminders.has(dismissalKey)) return;
        if (![sales.data, hours.data, actuals.data].every(Array.isArray)) return;
        const dates = missingSalesDates({
          yesterday, sales: sales.data, actualShifts: actuals.data,
          operatingHours: hours.data, includeYesterday,
        });
        setMissingDates(dates);
        setOpen((wasOpen) => dates.length > 0 && (wasOpen || !dismissed.current));
      } catch {
        // Failed reads are not evidence of missing sales. Retry on focus.
      }
    };
    check();
    window.addEventListener("focus", check);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      window.removeEventListener("focus", check);
    };
  }, [store.id, yesterday, startDate, salesUrl, includeYesterday, dismissalKey]);

  const close = () => {
    if (savingRef.current) return;
    dismissed.current = true;
    setOpen(false);
  };

  const later = () => {
    if (savingRef.current) return;
    deferredSalesReminders.add(dismissalKey);
    close();
  };

  const completeDate = (savedDate, alreadyEntered) => {
    onSaved();
    if (!mounted.current) return;
    setMissingDates((current) => current.filter((day) => day !== savedDate));
    if (missingDates.length === 1) setOpen(false);
    setError("");
    toast.success(alreadyEntered
      ? "Sales have already been entered for this day. The existing total was kept."
      : "Sales saved.");
  };

  const save = async (event) => {
    event.preventDefault();
    if (savingRef.current || !date || !validSalesAmount(amount)) return;
    savingRef.current = true;
    requestVersion.current += 1;
    setSaving(true);
    setError("");
    const savedDate = date;
    try {
      // The server checks and creates under its sales write lock. A preflight
      // GET cannot protect against two managers saving at the same time.
      await axios.post(API_add_manual_actual_sales + store.id, {
        date: savedDate, sales_value: Number(amount), create_only: true,
      }, getDefaultAxiosConfig());
      completeDate(savedDate, false);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === "actual_sales_already_exists") {
        completeDate(savedDate, true);
      } else if (mounted.current) {
        setError(err.response?.data?.error || "We couldn’t save sales. Please try again.");
      }
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  if (missingDates.length === 0 || deferredSalesReminders.has(dismissalKey)) return null;
  const dateLabel = formatSalesDate(date);
  const onlyYesterday = missingDates.length === 1 && date === yesterday;

  return (
    <>
      {!open && (
        <aside aria-label="Missing sales reminder" className="fixed bottom-[calc(var(--schedule-bottom-metrics-height,0px)+24px)] z-[60] flex max-w-md items-center gap-4 rounded-2xl border border-violet-200 bg-white p-4 shadow-lg sm:bottom-[calc(var(--schedule-bottom-metrics-height,0px)+12px)] sm:gap-3" style={{ left: bannerLeft, maxWidth: `calc(100vw - ${bannerLeft + 16}px)` }}>
          <div>
            <p className="text-sm font-semibold text-slate-900">{onlyYesterday ? "Yesterday’s sales are missing" : `${missingDates.length} ${missingDates.length === 1 ? "day needs" : "days need"} sales`}</p>
            <p className="mt-1 text-xs text-slate-600">{store.store_name} · {missingDates.length > 1 ? "Last 14 days" : dateLabel}</p>
          </div>
          <Button variant="contained" sx={{ ...primaryButtonSx, flexShrink: 0 }} onClick={() => setOpen(true)}>Enter sales</Button>
        </aside>
      )}
      <Modal open={open} onClose={close}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="sales-reminder-title"
          aria-describedby="sales-reminder-description"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)",
            maxWidth: 420,
            maxHeight: "calc(100dvh - 32px)",
            bgcolor: "background.paper",
            border: "1px solid #E2E8F0",
            borderRadius: "16px",
            boxShadow: 24,
            outline: "none",
            overflowY: "auto",
          }}
        >
          <form onSubmit={save}>
            <div className="border-b border-slate-100 px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
                    <EditIcon style={{ fontSize: "19px" }} />
                  </span>
                  <div className="min-w-0">
                    <h3 id="sales-reminder-title" className="text-lg font-semibold leading-6 text-slate-900">
                      {onlyYesterday ? "Enter yesterday’s sales" : "Enter missing sales"}
                    </h3>
                    <p className="mt-0.5 break-words text-xs text-slate-500">
                      {store.store_name} · {missingDates.length > 1 ? `${missingDates.length} days need sales` : dateLabel}
                    </p>
                  </div>
                </div>
                <IconButton
                  aria-label="Close sales reminder"
                  className="text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  onClick={close}
                  disabled={saving}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>

            <div className="px-6 py-5">
              <p id="sales-reminder-description" className="mb-4 text-sm leading-5 text-slate-600">
                Sales aren’t imported automatically for this location. Enter the final total to update actual sales and earned labor.
              </p>
              {missingDates.length > 1 && (
                <div className="mb-4">
                  <label id="sales-reminder-date-label" htmlFor="sales-reminder-date" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Sales date
                  </label>
                  <RightworkSelect
                    id="sales-reminder-date"
                    value={date}
                    disabled={saving}
                    onChange={(event) => { setSelectedDate(event.target.value); setError(""); }}
                    options={missingDates.map((day) => ({ value: day, label: formatSalesDate(day) }))}
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500">Missing totals from the last 14 days. Save each day to clear it.</p>
                </div>
              )}
              <label htmlFor="reminder-sales-total" className="mb-1.5 block text-sm font-medium text-slate-700">
                Actual sales total
              </label>
              <TextField
                id="reminder-sales-total"
                autoFocus
                fullWidth
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={saving}
                inputProps={{ min: 0, step: "0.01", inputMode: "decimal", "aria-describedby": "sales-reminder-hint" }}
                InputProps={{
                  startAdornment: <span className="mr-2 text-lg font-semibold text-slate-400">$</span>,
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "12px",
                    "& input": {
                      fontSize: "20px",
                      fontWeight: 650,
                      paddingTop: "14px",
                      paddingBottom: "14px",
                      MozAppearance: "textfield",
                    },
                    "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94A3B8" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6D28D9",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
              <p id="sales-reminder-hint" className="mt-2 text-xs leading-5 text-slate-500">
                Enter 0 if the location had no sales.
              </p>
              {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}

              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={later}
                  disabled={saving}
                >
                  Later
                </button>
                <button
                  type="submit"
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
                    validSalesAmount(amount) && !saving
                      ? "bg-violet-600 text-white hover:bg-violet-700"
                      : "cursor-not-allowed bg-slate-100 text-slate-400"
                  }`}
                  disabled={saving || !validSalesAmount(amount)}
                >
                  {saving ? "Saving..." : "Save sales"}
                </button>
              </div>
            </div>
          </form>
        </Box>
      </Modal>
    </>
  );
}

export default function ManualSalesReminder({ store, permissionRole, userId, onSaved, bannerLeft = 16 }) {
  const timezone = store?.timezone;
  const startQhnum = store?.start_qhnum;
  const [, setDate] = useState(() => previousBusinessDate(store || {}));
  useEffect(() => {
    const sync = () => setDate(previousBusinessDate({ timezone, start_qhnum: startQhnum }));
    sync();
    const timer = setInterval(sync, 60000);
    return () => clearInterval(timer);
  }, [timezone, startQhnum]);

  if (!canShowManualSalesReminder(store, permissionRole)) return null;
  // Compute immediately on a store switch, before the clock effect runs.
  const currentDate = previousBusinessDate(store);
  return <SalesReminderForDate key={`${userId}:${store.id}:${currentDate}`} store={store} date={currentDate} userId={userId} onSaved={onSaved} bannerLeft={bannerLeft} />;
}
